import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CrmSegmentInfo } from '@aviaconsole/shared';
import { capi } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { useI18n } from '../../i18n';
import { useToast } from '../../components/Toast';
import { Button, Chip, Input, Loading, Modal } from '../../components/ui';
import { segmentLabel, useSegments } from '../../lib/crm';

/** Add a segment by name (the key follows from it); calls back with the new segment. */
function useAddSegment(onAdded?: (s: CrmSegmentInfo) => void) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (body: { name: string; description?: string }) => capi<CrmSegmentInfo>('/crm/segments', { method: 'POST', json: body }),
    onSuccess: (s) => { qc.invalidateQueries({ queryKey: ['crm-segments'] }); toast.push(t('segAdded', { name: s.name })); onAdded?.(s); },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });
}

/** One line under the segment select in the account form: type a name, press add, and it is selected. */
export function NewSegmentInline({ onAdded }: { onAdded: (key: string) => void }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const add = useAddSegment((s) => { onAdded(s.key); setName(''); setOpen(false); });
  if (!open) return <Button size="sm" variant="ghost" icon="fas fa-plus" style={{ alignSelf: 'flex-start' }} onClick={() => setOpen(true)}>{t('segNew')}</Button>;
  const submit = () => { if (name.trim()) add.mutate({ name: name.trim() }); };
  return (
    <div className="row">
      <Input aria-label={t('segName')} placeholder={t('segName')} autoFocus value={name} maxLength={100} onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } if (e.key === 'Escape') setOpen(false); }} />
      <Button size="sm" variant="primary" disabled={!name.trim() || add.isPending} onClick={submit}>{t('segAdd')}</Button>
      <Button size="sm" onClick={() => setOpen(false)}>{t('cancel')}</Button>
    </div>
  );
}

/** The company's segments: add one, see how many accounts each holds, and (admin) remove an empty one. */
export function SegmentsModal({ onClose }: { onClose: () => void }) {
  const { t, lang } = useI18n();
  const { role } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const segments = useSegments();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const add = useAddSegment(() => { setName(''); setDescription(''); });
  const remove = useMutation({
    mutationFn: (key: string) => capi(`/crm/segments/${encodeURIComponent(key)}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['crm-segments'] }); toast.push(t('done')); },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });
  return (
    <Modal title={t('segTitle')} onClose={onClose}>
      <div className="stack md">
        <form className="stack md" onSubmit={(e) => { e.preventDefault(); if (name.trim()) add.mutate({ name: name.trim(), description: description.trim() || undefined }); }}>
          <div className="grid fields">
            <Input label={t('segName')} required maxLength={100} value={name} placeholder="Flight schools" onChange={(e) => setName(e.target.value)} />
            <Input label={t('segDescription')} maxLength={500} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <Button variant="primary" type="submit" icon="fas fa-plus" disabled={!name.trim() || add.isPending}>{t('segAdd')}</Button>
          </div>
        </form>
        {!segments.data ? <Loading error={segments.error} /> : (
          <table>
            <thead><tr><th style={{ textAlign: 'left' }}>{t('segName')}</th><th style={{ textAlign: 'left' }}>{t('segKey')}</th><th className="r">{t('segAccounts')}</th><th /></tr></thead>
            <tbody>
              {segments.data.map((s) => (
                <tr key={s.key}>
                  <td><div style={{ fontWeight: 600 }}>{segmentLabel(segments.data, s.key, lang)}</div>{s.description && <div className="sub">{s.description}</div>}</td>
                  <td className="ref">{s.key}</td>
                  <td className="r">{s.accountCount}</td>
                  <td className="r">
                    {s.builtIn ? <Chip tone="neutral">{t('segBuiltIn')}</Chip> : role === 'admin' && (
                      <Button size="sm" variant="ghost" icon="fas fa-trash" aria-label={t('segDelete')} title={s.accountCount ? t('segInUse') : t('segDelete')}
                        disabled={s.accountCount > 0 || remove.isPending} onClick={() => remove.mutate(s.key)} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="row" style={{ justifyContent: 'flex-end' }}><Button onClick={onClose}>{t('segClose')}</Button></div>
      </div>
    </Modal>
  );
}
